/**
 * @graphql cc
 */
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';
import gql from 'graphql-tag';

import { useSessionData } from '@confluence/session-data';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { getMonitoringClient } from '@confluence/monitoring';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { fg } from '@confluence/feature-gating';

import {
	ConfluenceSpaceRoleMode,
	type useSpaceRoleModeQueryVariables as useSpaceRoleModeQueryVariables$variables,
	type useSpaceRoleModeQuery_Query as useSpaceRoleModeQuery_Query$data,
} from './__types__/useSpaceRoleModeQuery';

const useSpaceRoleModeQuery = gql`
	query useSpaceRoleModeQuery($cloudId: ID!) {
		confluence_spaceRoleMode(cloudId: $cloudId)
	}
`;

export const useSpaceRoleMode = () => {
	// Putting these conditions ' || false' conditions here temporarily to avoid the eslint rule.
	// We are using the query data to replace a feature gate,
	// so we still want the isRBACEnabled to fall back on the prior FG value if the gateless FG is disabled for a safe rollout
	const isRbacBetaEnabled = fg('confluence_rbac_spaces_beta_opt_in') || false;
	const isGatelessFgEnabled = fg('confluence_space_roles_mode_gate') || false;
	const { cloudId } = useSessionData();
	const { data, loading } = useQuery<
		useSpaceRoleModeQuery_Query$data,
		useSpaceRoleModeQueryVariables$variables
	>(useSpaceRoleModeQuery, {
		variables: { cloudId },
		onError: (error) => {
			getMonitoringClient().submitError(error, {
				attribution: Attribution.PERMISSIONS_EXPERIENCE,
			});
			markErrorAsHandled(error);
		},
		skip: !isGatelessFgEnabled,
	});

	const isNotPreRoles = data?.confluence_spaceRoleMode
		? data.confluence_spaceRoleMode !== ConfluenceSpaceRoleMode.PRE_ROLES
		: false;
	const enabilityToCheck = isGatelessFgEnabled ? isNotPreRoles : isRbacBetaEnabled;

	return {
		spaceRoleMode: data?.confluence_spaceRoleMode,
		isRbacEnabled: enabilityToCheck,
		loading,
	};
};
